// ** React Imports
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Typography,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";

// ** Icon Imports
import Icon from "components/icon";

// ** Logic Imports
import { GetIndustries } from "./GetIndustries";
import { SaveIndustry } from "./SaveIndustry";
import { DeleteIndustry } from "./DeleteIndustry";
import { useSettingPermission } from "../permissions/SettingPermissions";

// ** Third-Party Imports
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import _ from 'lodash';
import { useTheme } from "@mui/material/styles";

export const useIndustryLogic = (toast) => {
  //#region Permissions
  const { SettingPermissionProps } = useSettingPermission();
  const {
    hasIndustrySavePermission,
    hasIndustryGetPermission,
    hasIndustryDeletePermission,
  } = SettingPermissionProps;
  //#endregion Permissions

  //#region Hooks
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const theme = useTheme();
  //#endregion

  //#region States
  const [loading, setLoading] = useState(true);
  const [openIndustryDlg, setOpenIndustryDlg] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rows, setRows] = useState([]);
  const [industry, setIndustry] = useState({
    industryID: 0,
    industryName: "",
    rate: 0,
    isActive: true,
  });
  const [existingIndustry, setExistingIndustry] = useState(industry);
  const [industryContent, setIndustryContent] = useState({
    entity: "create",
    title: "Create New Industry",
    btnText: "Save Industry"
  });
  const [errors, setErrors] = useState({
    industryName: false,
  });
  const [btnDisabled, setBtnDisabled] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  //#endregion States

  //#region Handlers

  useEffect(() => {
    // Add null checks and ensure proper comparison
    const isEqual = industry && existingIndustry &&  
                   industry.industryName === existingIndustry.industryName;
    setBtnDisabled(isEqual);
  }, [industry, existingIndustry]);

  useEffect(() => {
    if(hasIndustryGetPermission){
        fetchIndustries()
    }
  }, [hasIndustryGetPermission])

  // Filtered rows based on search query
  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    
    const query = searchQuery.toLowerCase();
    return rows.filter(row => 
      row.industryName?.toLowerCase().includes(query)
    );
  }, [rows, searchQuery]);

  // Search handler
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const fetchIndustries = async () => {
    if(!hasIndustryGetPermission) return;

    try {
      const response = await GetIndustries(navigate);

      if (!response.isError) {
        const formattedData = response
          .map((industry) => ({
            id: industry.industryID,
            industryID: industry.industryID,
            industryName: industry.industryName,
            companyID: industry.companyID
          }))
          .sort((a, b) => a.industryName.localeCompare(b.industryName)); 
        
        setRows(formattedData);
      }
    } catch (error) {
      toast.error(error || "Failed to fetch data");
    }
  };

  const handleOpenIndustryDlg = () => {
    setIndustryContent({ entity: "create", title: "Create New Industry", btnText: "Save Industry" });
    setOpenIndustryDlg(true);
  };

  const handleCloseIndustryDlg = () => { 
    setOpenIndustryDlg(false);
    resetIndustry();
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setIndustry((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    
    const newErrors = {
      industryName: !industry.industryName,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error !== false && error !== "")) return;

    try {
      const result = await SaveIndustry(industry, navigate);
      if (result && !result.isError) {
        toast.success(`Industry ${industryContent.entity === 'create' ? 'created' : 'updated'} successfully`);
        handleCloseIndustryDlg();
        fetchIndustries();
        resetIndustry();
      } else {
        toast.error(result?.message || `Failed to ${industryContent.entity === 'create' ? 'create' : 'update'} industry`);
      }
    } catch (error) {
      console.error("Error saving industry:", error);
    }
  };

  const resetIndustry = () => {
    setIndustry({
      industryID: 0,
      industryName: "",
    });
  };

  const showDeleteConfirmation = (id, companyID) => {
    MySwal.fire({
      title: "<strong>Are you sure?</strong>",
      html: "<span>You won't be able to revert this!</span>",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
      width: "390px",
      padding: "1rem",
      focusConfirm: false,
      showCloseButton: true,
      closeButtonHtml: "&times;",
      customClass: {
        popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
         cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
      },
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteIndustry(id, companyID);
      }
    });
  };

  const handleDeleteIndustry = async (id, companyID) => {
    if(companyID === 0){
      toast.error('Default Industries can not be deleted')
      return;
    }
    try {
      setLoading(true);

      const result = await DeleteIndustry(id, navigate);
      if (result && !result.isError) {
        fetchIndustries();
        toast.success("Industry deleted successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      } else {
        toast.error(result.message || "Industry failed to delete.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error("Error deleting Industry:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleIndustryEdit = (id) => {
    
    const industryToEdit = rows.find((industry) => industry.industryID === id);
    
    if (industryToEdit) {
        setIndustryContent({ entity: "edit", title: "Edit Industry", btnText: "Save Changes" });
        setIndustry((prev) => ({
            ...prev,
            industryID: industryToEdit.industryID,
            industryName: industryToEdit.industryName
        }))
        setExistingIndustry((prev) => ({
          ...prev,
          industryID: industryToEdit.industryID,
          industryName: industryToEdit.industryName
      }))
        setOpenIndustryDlg(true);
    }
  };

  //#endregion Handlers

  //#region Columns Configuration
  const columns = [
    {
      flex: 0.1,
      minWidth: 150,
      field: "industryName",
      headerName: "Industry Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.industryName}
            </Typography>
          </Box>
        );
      },
    },
    {
    flex: 0.1,
    field: "actions",
    minWidth: 120,
    maxWidth: 120,
    headerName: "Action",
    renderCell: (params) => {
        const { row } = params;
        return (
        <>
            <Tooltip title="Edit">
                <IconButton 
                    onClick={() => handleIndustryEdit(row.industryID)}
                >
                    <Icon icon="tabler:pencil" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton
                    color="error"
                    onClick={() => showDeleteConfirmation(row.industryID, row.companyID)}
                >
                    <Icon icon="tabler:trash" />
                </IconButton>
            </Tooltip>
        </>
        );
    },
    }
  ];
  //#endregion Columns Configuration

  const IndustryProps = {
    industryContent,
    resetIndustry,
    industry,
    setIndustry,
    existingIndustry,
    handleChange,
    errors,
    handleSave,
    rows,
    filteredRows,
    fetchIndustries,
    columns,
    paginationModel,
    setPaginationModel,
    openIndustryDlg,
    handleOpenIndustryDlg,
    handleCloseIndustryDlg,
    btnDisabled,
    // Search related props
    searchQuery,
    handleSearchChange,
  };

  return {
    IndustryProps,
  };
};